import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMySubmission } from 'src/presentation/hooks/useMySubmission';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';

/**
 * Compact banner shown above the EventScreen tiles, with the chosen programme to fold open under it.
 */
function MySubmissionBanner() {
	const eventStore = useEventStore();
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const { status, submission } = useMySubmission(eventStore.activeEventUuid);
	const [expanded, setExpanded] = useState(false);

	if (status !== 'success' || submission === null) {
		return null;
	}

	const programme = submission.programme ?? [];

	return (
		<View style={styles.banner} testID="my-submission-banner">
			<TouchableOpacity
				accessibilityRole="button"
				activeOpacity={0.8}
				style={styles.top}
				testID="my-submission-banner-open"
				onPress={() => navigation.navigate(Constants.screenMySubmission)}
			>
				<View style={styles.iconBadge}>
					<Ionicons color={Colors.white} name="qr-code-outline" size={24} />
				</View>
				<View style={styles.textContainer}>
					<Text numberOfLines={1} style={styles.title}>
						{Translations.mySubmissionBannerTitle}
					</Text>
					<Text numberOfLines={1} style={styles.subtitle}>
						{Translations.mySubmissionBannerSubtitle}
					</Text>
				</View>
				<Ionicons color={Colors.greyIcon} name="chevron-forward" size={20} />
			</TouchableOpacity>

			{programme.length > 0 ? (
				<View style={styles.programme}>
					<TouchableOpacity
						accessibilityRole="button"
						accessibilityState={{ expanded }}
						activeOpacity={0.8}
						style={styles.programmeToggle}
						testID="my-submission-banner-programme-toggle"
						onPress={() => setExpanded((open) => !open)}
					>
						<Text style={styles.programmeLabel}>{Translations.mySubmissionProgrammeTitle}</Text>
						<Ionicons color={Colors.greyDark} name={expanded ? 'chevron-up' : 'chevron-down'} size={18} />
					</TouchableOpacity>
					{expanded
						? programme.map((day, dayIndex) => (
								<View key={`${day.day}-${dayIndex}`} style={styles.programmeDay}>
									{day.day !== '' ? <Text style={styles.programmeDayTitle}>{day.day}</Text> : null}
									{day.lines.map((line, lineIndex) => (
										<Text key={`${line}-${lineIndex}`} style={styles.programmeLine}>
											{line}
										</Text>
									))}
								</View>
							))
						: null}
				</View>
			) : null}
		</View>
	);
}

export default MySubmissionBanner;

const styles = StyleSheet.create({
	banner: {
		backgroundColor: Colors.white,
		borderRadius: 12,
		borderWidth: Dimens.borderWidth1,
		borderColor: Colors.greyBorder,
		marginHorizontal: Dimens.margin20,
		marginTop: Dimens.margin24,
	},
	top: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: Dimens.padding12,
		paddingHorizontal: Dimens.padding16,
		gap: Dimens.margin12,
	},
	iconBadge: {
		width: 44,
		height: 44,
		borderRadius: 22,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: Colors.secondary,
	},
	textContainer: {
		flex: 1,
	},
	title: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	subtitle: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize13,
		color: Colors.greyDark,
		marginTop: Dimens.margin2,
	},
	programme: {
		borderTopWidth: Dimens.borderWidth1,
		borderTopColor: Colors.greyBorder,
		paddingHorizontal: Dimens.padding16,
		paddingBottom: Dimens.padding12,
	},
	programmeToggle: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: Dimens.padding12,
	},
	programmeLabel: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize12,
		color: Colors.greyDark,
		letterSpacing: 1,
		textTransform: 'uppercase',
	},
	programmeDay: {
		marginTop: Dimens.margin8,
	},
	programmeDayTitle: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize14,
		color: Colors.greyTextDark,
		marginBottom: Dimens.margin2,
	},
	programmeLine: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight20,
		color: Colors.greyTextDark,
	},
});
